import { useEffect, useRef, type ChangeEvent, type ComponentType, type Dispatch, type KeyboardEvent, type ReactNode } from 'react';
import { formatDate } from '../ui/kit.tsx';
import { cellText, diffWords, type GridAction, type GridRow, type GridState } from './grid.ts';
import type { Field, FieldValue, Proposal, ProposalKind, ProposalRow } from './types.ts';

/**
 * Fixed grid building blocks, chosen by proposal kind. The model only ever sends data (a proposal); it never
 * writes UI. Every grid is a GridTable with columns: the coupon, one current → proposed column per editable field
 * (difference highlighted), and the warnings.
 */

export interface GridProps {
  proposal: Proposal;
  state: GridState;
  dispatch: Dispatch<GridAction>;
  readOnly: boolean;
}

interface ValueColumn {
  field: Field;
  label: string;
  /** Long text edits in a textarea. */
  multiline?: boolean;
  show: (value: FieldValue | undefined) => string;
}

const textValue = (value: FieldValue | undefined) => (typeof value === 'string' ? value : '');
const endValue = (value: FieldValue | undefined) => (value === 'now' ? 'now (when you save)' : value === null || value === undefined ? 'no end date' : formatDate(value as string));
const hiddenValue = (value: FieldValue | undefined) => (value === true ? 'hidden' : 'shown');

function Diff({ from, to, changed }: { from: string; to: string; changed: boolean }) {
  if (!changed) return <span className="cg-same">{to}</span>;
  const parts = diffWords(from, to);
  return (
    <span className="cg-diff">
      <span className="cg-from" aria-label="current">
        {parts.from.map((part, i) => (part.changed ? <del key={i}>{part.text}</del> : <span key={i}>{part.text}</span>))}
      </span>
      <span className="cg-to" aria-label="proposed">
        {parts.to.map((part, i) => (part.changed ? <mark key={i}>{part.text}</mark> : <span key={i}>{part.text}</span>))}
      </span>
    </span>
  );
}

function CellEditor({ state, dispatch, multiline }: { state: GridState; dispatch: Dispatch<GridAction>; multiline: boolean }) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  // Back to the grid so j/k/x keep working after the edit.
  const backToGrid = () => (ref.current?.closest('[data-grid]') as HTMLElement | null)?.focus();
  // Esc must not commit through the blur that moving focus causes.
  const cancelled = useRef(false);
  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelled.current = true;
      dispatch({ type: 'cancelEdit' });
      backToGrid();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      dispatch({ type: 'commitEdit' });
      backToGrid();
    }
  };
  const props = {
    ref,
    className: 'cg-input',
    value: state.editing?.draft ?? '',
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => dispatch({ type: 'draft', value: event.target.value }),
    onKeyDown,
    onBlur: () => {
      if (!cancelled.current) dispatch({ type: 'commitEdit' });
    },
    'aria-label': `Edit ${state.editing?.field ?? 'value'}`,
  };
  return multiline ? <textarea rows={3} {...props} /> : <input {...props} />;
}

function Warnings({ row }: { row: ProposalRow }) {
  if (row.warnings.length === 0) return <span className="muted">—</span>;
  return (
    <ul className="cg-warnings">
      {row.warnings.map((warning, i) => (
        <li key={i} className={`cg-w-${warning.level}`}>
          {warning.message}
        </li>
      ))}
    </ul>
  );
}

function GridTable({ proposal, state, dispatch, readOnly, columns, coupon }: GridProps & { columns: ValueColumn[]; coupon: (row: ProposalRow) => ReactNode }) {
  const body = useRef<HTMLTableSectionElement>(null);
  const shown = columns.filter((column) => proposal.fields.includes(column.field));
  const from = Math.min(state.anchor ?? state.cursor, state.cursor);
  const to = Math.max(state.anchor ?? state.cursor, state.cursor);
  useEffect(() => {
    body.current?.querySelector<HTMLElement>(`[data-row="${state.cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [state.cursor]);
  const rows = new Map(state.rows.map((row) => [row.id, row]));
  return (
    <table className="cg-table">
      <thead>
        <tr>
          <th className="cg-inc" aria-label="Included">
            ✓
          </th>
          <th>Coupon</th>
          {shown.map((column, i) => (
            <th key={column.field} className={i === state.column ? 'cg-col-active' : undefined}>
              {column.label} <span className="muted">current → proposed</span>
            </th>
          ))}
          <th>Warnings</th>
        </tr>
      </thead>
      <tbody ref={body}>
        {proposal.rows.map((row, index) => {
          const grid = rows.get(row.id) as GridRow;
          const cursor = index === state.cursor;
          const selected = index >= from && index <= to && (state.anchor !== null || cursor);
          return (
            <tr key={row.id} data-row={index} className={[cursor ? 'cg-cursor' : '', selected ? 'cg-selected' : '', grid.included ? '' : 'cg-excluded'].join(' ').trim() || undefined} aria-selected={cursor}>
              <td className="cg-inc">
                <input
                  type="checkbox"
                  checked={grid.included}
                  disabled={readOnly || grid.blocked}
                  tabIndex={-1}
                  aria-label={`Include ${row.merchant} ${row.couponRef}${row.siteName ? ` on ${row.siteName}` : ''}`}
                  onChange={() => {
                    dispatch({ type: 'move', delta: index - state.cursor });
                    dispatch({ type: 'toggle' });
                  }}
                />
              </td>
              <td className="cg-coupon">{coupon(row)}</td>
              {shown.map((column, i) => {
                const editing = state.editing && state.editing.rowId === row.id && state.editing.field === column.field;
                const proposed = grid.proposed[column.field];
                const current = row.current[column.field];
                const changed = (proposed ?? null) !== (current ?? null);
                return (
                  <td
                    key={column.field}
                    className={[cursor && i === state.column ? 'cg-cell-active' : '', grid.edited && changed ? 'cg-edited' : ''].join(' ').trim() || undefined}
                    onDoubleClick={() => {
                      if (readOnly) return;
                      dispatch({ type: 'move', delta: index - state.cursor });
                      dispatch({ type: 'startEdit', field: column.field, value: cellText(column.field, proposed) });
                    }}
                  >
                    {editing ? <CellEditor state={state} dispatch={dispatch} multiline={column.multiline === true} /> : <Diff from={column.show(current)} to={column.show(proposed)} changed={changed} />}
                  </td>
                );
              })}
              <td className="cg-warn-cell">
                <Warnings row={row} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CouponCell({ row, site = true }: { row: ProposalRow; site?: boolean }) {
  return (
    <>
      <strong>{row.merchant}</strong> <span className="muted">{row.market}</span>
      <div className="mono muted">
        {row.couponRef}
        {row.code ? <> · {row.code}</> : null}
      </div>
      {site && row.siteName ? <div>{row.siteName}{row.live ? <span className="badge badge-ok">live</span> : null}</div> : null}
      {!site && row.sites.length ? <div className="muted">on {row.sites.join(', ')}</div> : null}
    </>
  );
}

export function SiteTextGrid(props: GridProps) {
  return (
    <GridTable
      {...props}
      coupon={(row) => <CouponCell row={row} />}
      columns={[
        { field: 'title', label: 'Title', show: textValue },
        { field: 'description', label: 'Description', multiline: true, show: textValue },
      ]}
    />
  );
}

export function VisibilityGrid(props: GridProps) {
  return <GridTable {...props} coupon={(row) => <CouponCell row={row} />} columns={[{ field: 'hidden', label: 'On the site', show: hiddenValue }]} />;
}

export function EndDateGrid(props: GridProps) {
  return <GridTable {...props} coupon={(row) => <CouponCell row={row} site={false} />} columns={[{ field: 'endsAt', label: 'End date (every site)', show: endValue }]} />;
}

export function ConditionsGrid(props: GridProps) {
  return <GridTable {...props} coupon={(row) => <CouponCell row={row} site={false} />} columns={[{ field: 'conditions', label: 'Conditions (every site)', multiline: true, show: textValue }]} />;
}

/** The only place a proposal kind becomes UI. */
export const GRID_COMPONENTS: Readonly<Record<ProposalKind, ComponentType<GridProps>>> = {
  site_text: SiteTextGrid,
  visibility: VisibilityGrid,
  end_date: EndDateGrid,
  conditions: ConditionsGrid,
};

/** The editable fields a grid of this proposal shows, in column order (h/l moves between them). */
export const gridFields = (proposal: Proposal): Field[] => {
  const order: Field[] = ['title', 'description', 'conditions', 'hidden', 'endsAt'];
  return order.filter((field) => proposal.fields.includes(field));
};
